import React, { useState, useCallback, useMemo, memo } from 'react';
import {
  TextField, Button, Box, Typography,
  InputAdornment, IconButton, Fade, Alert,
  Card, alpha, useTheme, Dialog,
  DialogTitle, DialogContent, Stack, Stepper,
  Step, StepLabel, useMediaQuery, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem, FormHelperText, Grid
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  LockOutlined,
  Visibility,
  VisibilityOff,
  EmailOutlined,
  VpnKey,
  PersonOutline,
  School,
  PhoneOutlined,
  PeopleAlt,
  Face,
  EventAvailable
} from '@mui/icons-material';
import api from '../utils/Api';
import { useAuth } from '../contexts/AuthContext';
import { getDefaultDashboardPath } from '../utils/permissions';
import { ColorModeContext } from '../App';
// Make sure to save your image to the path below:
import logo from '../assets/school_logo.png';

// 🎯 Memoized Form Components
const LoginTextField = memo(({
  label,
  name,
  type = 'text',
  value,
  onChange,
  onKeyPress,
  showPassword,
  onTogglePassword,
  size,
  theme,
  isMobile,
  startIcon: StartIcon,
  error,
  helperText
}) => (
  <TextField
    fullWidth
    margin="normal"
    variant="outlined"
    label={label}
    name={name}
    type={type}
    value={value}
    onChange={onChange}
    onKeyPress={onKeyPress}
    size={size}
    error={error}
    helperText={helperText}
    InputProps={{
      startAdornment: StartIcon && (
        <InputAdornment position="start">
          <StartIcon sx={{
            color: 'primary.main',
            fontSize: isMobile ? '1.2rem' : '1.5rem'
          }} />
        </InputAdornment>
      ),
      endAdornment: onTogglePassword && (
        <InputAdornment position="end">
          <IconButton
            onClick={onTogglePassword}
            edge="end"
            size={size}
            sx={{
              color: 'text.secondary',
              '&:hover': {
                color: 'primary.main',
                bgcolor: alpha(theme.palette.primary.main, 0.1)
              }
            }}
          >
            {showPassword ? <VisibilityOff /> : <Visibility />}
          </IconButton>
        </InputAdornment>
      )
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        fontSize: isMobile ? '1.1rem' : '1rem',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
        backdropFilter: 'blur(10px)',
        '&:hover': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
        },
        '& fieldset': {
          borderWidth: '1px',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        '&:hover fieldset': {
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#0a84ff',
          borderWidth: '1px',
        },
        '&.Mui-focused': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.05)',
          boxShadow: '0 0 0 4px rgba(10, 132, 255, 0.15)',
        },
        '& .MuiInputBase-input': {
          padding: isMobile ? '16px 14px' : '16.5px 14px',
          '&:-webkit-autofill': {
            WebkitBoxShadow: theme.palette.mode === 'dark' ? '0 0 0 1000px #1e293b inset !important' : '0 0 0 1000px #ffffff inset !important',
            WebkitTextFillColor: `${theme.palette.text.primary} !important`,
            transition: 'background-color 5000s ease-in-out 0s',
          }
        }
      },
      '& .MuiInputLabel-root': {
        fontWeight: 500,
        fontSize: isMobile ? '1rem' : '1rem',
        color: theme.palette.text.secondary
      }
    }}
  />
));

// 🎯 Memoized Loading Spinner
const LoadingSpinner = memo(({ size = 'medium', text = 'Loading...' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{
      width: size === 'small' ? 14 : size === 'medium' ? 16 : 20,
      height: size === 'small' ? 14 : size === 'medium' ? 16 : 20,
      border: '2px solid transparent',
      borderTop: '2px solid white',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    {text}
  </Box>
));

// 🎯 Memoized Reset Modal Steps
const ResetStepOne = memo(({
  adminResetEmail,
  setAdminResetEmail,
  resetMessage,
  isResetting,
  onRequestReset,
  isMobile,
  theme
}) => (
  <>
    <Typography
      variant={isMobile ? "caption" : "body2"}
      sx={{
        textAlign: 'center',
        color: 'text.secondary',
        lineHeight: 1.6
      }}
    >
      Enter your admin email address to receive a reset code.
      <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
        <strong>Note:</strong> This reset process is for admin accounts only.
      </Alert>
    </Typography>

    <LoginTextField
      label="Admin Email Address"
      type="email"
      value={adminResetEmail}
      onChange={(e) => setAdminResetEmail(e.target.value)}
      size="medium"
      theme={theme}
      isMobile={isMobile}
      startIcon={EmailOutlined}
    />

    {resetMessage && (
      <Alert
        severity={
          resetMessage.startsWith('✅') || resetMessage.startsWith('🔒')
            ? 'info'
            : resetMessage.startsWith('⚠️')
              ? 'warning'
              : 'error'
        }
        sx={{ borderRadius: 2 }}
      >
        {resetMessage}
      </Alert>
    )}

    <Button
      variant="contained"
      onClick={onRequestReset}
      disabled={isResetting}
      size={isMobile ? "medium" : "large"}
      sx={{
        py: isMobile ? 1.25 : 1.5,
        fontSize: isMobile ? '0.9rem' : '1rem',
        fontWeight: 600,
        borderRadius: '12px',
        textTransform: 'none',
        backgroundColor: '#0a84ff',
        color: '#fff',
        boxShadow: '0 4px 14px 0 rgba(10, 132, 255, 0.39)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 6px 20px rgba(10, 132, 255, 0.23)',
          backgroundColor: '#007aff',
        },
        '&:active': {
          transform: 'scale(0.97)',
        }
      }}
    >
      {isResetting ? (
        <LoadingSpinner
          size="medium"
          text={isMobile ? 'Requesting...' : 'Requesting Code...'}
        />
      ) : (
        'Get Reset Code'
      )}
    </Button>
  </>
));

const ResetStepTwo = memo(({
  adminResetCode,
  setAdminResetCode,
  adminNewPassword,
  setAdminNewPassword,
  adminConfirmPassword,
  setAdminConfirmPassword,
  generatedCode,
  resetMessage,
  isResetting,
  onReset,
  onBack,
  isMobile,
  theme
}) => (
  <>
    <Typography
      variant={isMobile ? "caption" : "body2"}
      sx={{
        textAlign: 'center',
        color: 'text.secondary',
        lineHeight: 1.6
      }}
    >
      Enter the reset code and your new password.
      {generatedCode && (
        <Alert severity="info" sx={{ mt: 1, borderRadius: 2 }}>
          <strong>Your reset code: {generatedCode}</strong> (Expires in 15 minutes)
        </Alert>
      )}
    </Typography>

    <LoginTextField
      label="Reset Code"
      value={adminResetCode}
      onChange={(e) => setAdminResetCode(e.target.value)}
      size="medium"
      theme={theme}
      isMobile={isMobile}
      startIcon={VpnKey}
      placeholder="Enter 6-digit code"
    />

    <LoginTextField
      label="New Password"
      type="password"
      value={adminNewPassword}
      onChange={(e) => setAdminNewPassword(e.target.value)}
      size="medium"
      theme={theme}
      isMobile={isMobile}
      startIcon={LockOutlined}
    />

    <LoginTextField
      label="Confirm New Password"
      type="password"
      value={adminConfirmPassword}
      onChange={(e) => setAdminConfirmPassword(e.target.value)}
      size="medium"
      theme={theme}
      isMobile={isMobile}
      startIcon={LockOutlined}
    />

    {resetMessage && (
      <Alert
        severity={
          resetMessage.startsWith('✅')
            ? 'success'
            : resetMessage.startsWith('⚠️')
              ? 'warning'
              : 'error'
        }
        sx={{ borderRadius: 2 }}
      >
        {resetMessage}
      </Alert>
    )}

    <Box sx={{
      display: 'flex',
      gap: 1,
      flexDirection: isMobile ? 'column' : 'row'
    }}>
      <Button
        variant="outlined"
        onClick={onBack}
        size={isMobile ? "medium" : "large"}
        sx={{
          flex: isMobile ? 'none' : 1,
          py: isMobile ? 1 : 1.5,
          textTransform: 'none',
          borderRadius: '12px',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)',
          color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
          order: isMobile ? 2 : 1,
          '&:hover': {
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.2)',
          }
        }}
      >
        Back
      </Button>
      <Button
        variant="contained"
        onClick={onReset}
        disabled={isResetting}
        size={isMobile ? "medium" : "large"}
        sx={{
          flex: isMobile ? 'none' : 2,
          py: isMobile ? 1 : 1.5,
          fontSize: isMobile ? '0.9rem' : '1rem',
          fontWeight: 600,
          borderRadius: '12px',
          textTransform: 'none',
          backgroundColor: '#0a84ff',
          color: '#fff',
          boxShadow: '0 4px 14px 0 rgba(10, 132, 255, 0.39)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          order: isMobile ? 1 : 2,
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 6px 20px rgba(10, 132, 255, 0.23)',
            backgroundColor: '#007aff',
          },
          '&:active': {
            transform: 'scale(0.97)',
          }
        }}
      >
        {isResetting ? (
          <LoadingSpinner
            size="medium"
            text={isMobile ? 'Resetting...' : 'Resetting...'}
          />
        ) : (
          'Reset Password'
        )}
      </Button>
    </Box>
  </>
));

// 🎯 Login Form Component
const LoginForm = memo(({
  theme,
  isMobile,
  handleChange,
  form,
  handleKeyPress,
  showPassword,
  togglePasswordVisibility,
  error,
  handleSubmit,
  isLoading,
  setResetModalOpen
}) => (
  <Box>
    <LoginTextField
      label="Email Address"
      name="email"
      value={form.email}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      size="medium"
      theme={theme}
      isMobile={isMobile}
      startIcon={EmailOutlined}
    />

    <LoginTextField
      label="Password"
      name="password"
      type={showPassword ? 'text' : 'password'}
      value={form.password}
      onChange={handleChange}
      onKeyPress={handleKeyPress}
      size="medium"
      theme={theme}
      isMobile={isMobile}
      startIcon={LockOutlined}
      showPassword={showPassword}
      onTogglePassword={togglePasswordVisibility}
    />

    {error && (
      <Alert
        severity="error"
        sx={{
          mt: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'error.light',
          bgcolor: alpha(theme.palette.error.main, 0.05),
          '& .MuiAlert-icon': {
            color: 'error.main'
          }
        }}
      >
        {error}
      </Alert>
    )}

    <Button
      fullWidth
      variant="contained"
      onClick={handleSubmit}
      disabled={isLoading}
      size={isMobile ? "medium" : "large"}
      sx={{
        py: isMobile ? 1.5 : 1.8,
        mt: 3,
        fontSize: isMobile ? '1rem' : '1.1rem',
        fontWeight: 600,
        borderRadius: '12px',
        textTransform: 'none',
        letterSpacing: '0.3px',
        backgroundColor: '#0f172a',
        color: '#fff',
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.35)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: 'translateY(-1px)',
          boxShadow: '0 16px 34px rgba(15, 23, 42, 0.45)',
          backgroundColor: '#111c33',
        },
        '&:active': {
          transform: 'scale(0.97)',
        }
      }}
    >
      {isLoading ? (
        <LoadingSpinner
          size={isMobile ? "medium" : "large"}
          text={isMobile ? 'Signing In...' : 'Signing In...'}
        />
      ) : (
        'Sign In'
      )}
    </Button>

    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Typography
        variant={isMobile ? "caption" : "body2"}
        onClick={() => setResetModalOpen(true)}
        sx={{
          color: 'primary.main',
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          '&:hover': {
            color: 'secondary.main',
            transform: 'translateX(4px)'
          }
        }}
      >
        Forgot your password?
        <Box component="span" sx={{ fontSize: isMobile ? '1rem' : '1.2rem' }}>→</Box>
      </Typography>
    </Box>
  </Box>
));

// 🎯 Register Form Component
const RegisterForm = memo(({
  theme,
  isMobile,
  registerForm,
  handleRegisterChange,
  registerErrors,
  registerApiError,
  showRegisterPassword,
  toggleRegisterPasswordVisibility,
  handleRegisterSubmit,
  isRegistering
}) => {
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') handleRegisterSubmit();
  }, [handleRegisterSubmit]);

  return (
    <Box>
      <LoginTextField
        label="Full Name"
        name="name"
        value={registerForm.name}
        onChange={handleRegisterChange}
        onKeyPress={handleKeyPress}
        size="medium"
        theme={theme}
        isMobile={isMobile}
        startIcon={PersonOutline}
        error={!!registerErrors.name}
        helperText={registerErrors.name}
      />

      <LoginTextField
        label="School Name"
        name="schoolName"
        value={registerForm.schoolName}
        onChange={handleRegisterChange}
        onKeyPress={handleKeyPress}
        size="medium"
        theme={theme}
        isMobile={isMobile}
        startIcon={School}
        error={!!registerErrors.schoolName}
        helperText={registerErrors.schoolName}
      />

      <LoginTextField
        label="Phone Number"
        name="phone"
        type="tel"
        value={registerForm.phone}
        onChange={handleRegisterChange}
        onKeyPress={handleKeyPress}
        size="medium"
        theme={theme}
        isMobile={isMobile}
        startIcon={PhoneOutlined}
        error={!!registerErrors.phone}
        helperText={registerErrors.phone}
      />

      <FormControl fullWidth margin="normal" error={!!registerErrors.schoolType}>
        <InputLabel id="school-type-label">School Type</InputLabel>
        <Select
          labelId="school-type-label"
          name="schoolType"
          value={registerForm.schoolType || 'Private'}
          label="School Type"
          onChange={handleRegisterChange}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              borderRadius: isMobile ? 2 : 3,
              borderWidth: '1px',
              borderColor: alpha(theme.palette.primary.main, 0.2)
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
              borderWidth: '2px'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
              borderWidth: '2px',
              boxShadow: 'none'
            },
            fontSize: isMobile ? '1.1rem' : '1rem'
          }}
        >
          <MenuItem value="Private">Private</MenuItem>
          <MenuItem value="Government">Government (Basic)</MenuItem>
        </Select>
        {registerErrors.schoolType && (
          <FormHelperText>{registerErrors.schoolType}</FormHelperText>
        )}
      </FormControl>

      <LoginTextField
        label="Email Address"
        name="email"
        type="email"
        value={registerForm.email}
        onChange={handleRegisterChange}
        onKeyPress={handleKeyPress}
        size="medium"
        theme={theme}
        isMobile={isMobile}
        startIcon={EmailOutlined}
        error={!!registerErrors.email}
        helperText={registerErrors.email}
      />

      <LoginTextField
        label="Password"
        name="password"
        type={showRegisterPassword ? 'text' : 'password'}
        value={registerForm.password}
        onChange={handleRegisterChange}
        onKeyPress={handleKeyPress}
        size="medium"
        theme={theme}
        isMobile={isMobile}
        startIcon={LockOutlined}
        showPassword={showRegisterPassword}
        onTogglePassword={toggleRegisterPasswordVisibility}
        error={!!registerErrors.password}
        helperText={registerErrors.password || 'Minimum 8 characters'}
      />

      <LoginTextField
        label="Confirm Password"
        name="confirmPassword"
        type={showRegisterPassword ? 'text' : 'password'}
        value={registerForm.confirmPassword}
        onChange={handleRegisterChange}
        onKeyPress={handleKeyPress}
        size="medium"
        theme={theme}
        isMobile={isMobile}
        startIcon={LockOutlined}
        error={!!registerErrors.confirmPassword}
        helperText={registerErrors.confirmPassword}
      />

      {registerApiError && (
        <Alert
          severity="error"
          sx={{
            mt: 2,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'error.light',
            bgcolor: alpha(theme.palette.error.main, 0.05),
            '& .MuiAlert-icon': {
              color: 'error.main'
            }
          }}
        >
          {registerApiError}
        </Alert>
      )}

      <Button
        fullWidth
        variant="contained"
        onClick={handleRegisterSubmit}
        disabled={isRegistering}
        size={isMobile ? "medium" : "large"}
        sx={{
          py: isMobile ? 1.5 : 1.8,
          mt: 3,
          fontSize: isMobile ? '1rem' : '1.1rem',
          fontWeight: 700,
          borderRadius: isMobile ? 2 : 3,
          textTransform: 'none',
          letterSpacing: '0.5px',
          backgroundColor: '#0f172a',
          boxShadow: isMobile
            ? '0 10px 24px rgba(15, 23, 42, 0.32)'
            : '0 12px 28px rgba(15, 23, 42, 0.35)',
          transition: 'all 0.3s ease-in-out',
          position: 'relative',
          overflow: 'hidden',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: isMobile
              ? '0 14px 28px rgba(15, 23, 42, 0.42)'
              : '0 16px 34px rgba(15, 23, 42, 0.45)',
            backgroundColor: '#111c33',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
            transition: 'left 0.5s'
          },
          '&:hover::before': {
            left: '100%'
          }
        }}
      >
        {isRegistering ? (
          <LoadingSpinner
            size={isMobile ? "medium" : "large"}
            text={isMobile ? 'Creating...' : 'Creating Account...'}
          />
        ) : (
          'Create Account'
        )}
      </Button>
    </Box>
  );
});

// 👁️ Custom Hook for Intersection Observer
const useInView = (options) => {
  const [isInView, setIsInView] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
        observer.disconnect(); // Play animation only once
      }
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }
    return () => observer.disconnect();
  }, [options]);

  return [ref, isInView];
};

const TrustSection = memo(({ isMobile }) => {
  const stats = [
    { icon: School, count: '50+', label: 'Schools', color: '#06b6d4' },
    { icon: PeopleAlt, count: '1,200+', label: 'Teachers', color: '#10b981' },
    { icon: Face, count: '15,000+', label: 'Students', color: '#f59e0b' },
    { icon: EventAvailable, count: '2M+', label: 'Attendance', color: '#8b5cf6' }
  ];
  const [ref, isInView] = useInView({ threshold: 0.1 });

  return (
    <Fade in timeout={1200}>
      <Box ref={ref} sx={{ width: '100%', zIndex: 1, position: 'relative' }}>
        <Typography variant={isMobile ? "h4" : "h2"} sx={{ color: 'white', fontWeight: 600, letterSpacing: '-1px', mb: 2, textAlign: isMobile ? 'center' : 'left', lineHeight: 1.1, opacity: 0, animation: isInView ? 'fadeUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'none', animationDelay: '0.1s' }}>
          Transforming school management.
        </Typography>
        <Typography variant={isMobile ? "body1" : "h5"} sx={{ color: 'rgba(255,255,255,0.6)', mb: isMobile ? 4 : 8, maxWidth: 600, mx: isMobile ? 'auto' : 0, textAlign: isMobile ? 'center' : 'left', fontWeight: 400, letterSpacing: '-0.5px', opacity: 0, animation: isInView ? 'fadeUp 1s cubic-bezier(0.16, 1, 0.3, 1) forwards' : 'none', animationDelay: '0.2s' }}>
          Through smart attendance, communication, learning tools, and administration.
        </Typography>

        <Grid container spacing={isMobile ? 1.5 : 3} justifyContent="flex-start" sx={{ maxWidth: 'lg' }}>
          {stats.map((stat, idx) => (
            <Grid item xs={6} sm={6} key={idx}>
              <Card sx={{
                p: isMobile ? 2 : 2.5,
                borderRadius: '20px',
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(24px)',
                WebkitBackdropFilter: 'blur(24px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white',
                opacity: 0,
                animation: isInView ? 'fadeUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards' : 'none',
                animationDelay: `${0.6 + idx * 0.15}s`,
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.2)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                textAlign: 'center',
                '&:hover': {
                  transform: 'translateY(-4px) scale(1.02)',
                  background: 'rgba(255, 255, 255, 0.08)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                  '& .stat-icon': {
                    transform: 'scale(1.1)'
                  }
                }
              }}>
                <stat.icon className="stat-icon" sx={{ fontSize: isMobile ? 28 : 36, color: stat.color, mb: 0.5, transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)' }} />
                <Box>
                  <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 300, color: 'white', letterSpacing: '-0.5px' }}>{stat.count}</Typography>
                  <Typography variant={isMobile ? "caption" : "body2"} sx={{ fontWeight: 500, opacity: 0.7, color: 'rgba(255, 255, 255, 0.9)', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem' }}>{stat.label}</Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Fade>
  );
});

// 🎯 Main Auth Component with Tabs
const AuthPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { setAuthenticatedUser } = useAuth();
  const colorMode = React.useContext(ColorModeContext);

  React.useEffect(() => {
    if (theme.palette.mode === 'dark') {
      colorMode.toggleColorMode();
    }
  }, [theme.palette.mode, colorMode]);

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // 🎯 Login States
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // 🎯 Register States
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    schoolName: '',
    schoolType: 'Private',
    role: 'admin' // Default to admin
  });

  const [registerErrors, setRegisterErrors] = useState({});
  const [registerApiError, setRegisterApiError] = useState('');
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // Reset Password Modal States
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetStep, setResetStep] = useState(0);

  // Admin reset states
  const [adminResetEmail, setAdminResetEmail] = useState('');
  const [adminResetCode, setAdminResetCode] = useState('');
  const [adminNewPassword, setAdminNewPassword] = useState('');
  const [adminConfirmPassword, setAdminConfirmPassword] = useState('');

  const [resetMessage, setResetMessage] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');

  // 🎯 Memoized event handlers for Login
  const handleChange = useCallback((e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');

      const res = await api.post('/auth/login', form);

      const { user, token } = res.data;

      if (user.role !== 'admin' && user.role !== 'superadmin') {
        // Clear everything if role mismatch
        await api.post('/auth/logout'); // Call backend logout to clear cookie
        localStorage.removeItem('user');
        setError('Access denied: only admins can log in here');
        setIsLoading(false);
        return;
      }

      // 🔒 Token is now stored in HttpOnly cookie AND localStorage for API access
      setAuthenticatedUser(user, token);
      navigate(getDefaultDashboardPath(user));
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [form, navigate, setAuthenticatedUser]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter') handleSubmit();
  }, [handleSubmit]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  // 🎯 Event handlers for Register
  const handleRegisterChange = useCallback((e) => {
    const { name, value } = e.target;
    setRegisterForm(prev => ({ ...prev, [name]: value }));

    if (registerErrors[name]) {
      setRegisterErrors(prev => ({ ...prev, [name]: '' }));
    }
  }, [registerErrors]);

  const validateRegisterForm = useCallback(() => {
    const newErrors = {};

    if (!registerForm.name.trim()) newErrors.name = 'Name is required';
    if (!registerForm.schoolName.trim()) newErrors.schoolName = 'School name is required';
    if (!registerForm.phone.trim()) newErrors.phone = 'Phone number is required';
    else if (!/^0\d{9}$/.test(registerForm.phone.trim())) newErrors.phone = 'Invalid Ghana phone number (e.g. 024XXXXXXX)';

    if (!registerForm.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(registerForm.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!registerForm.password) {
      newErrors.password = 'Password is required';
    } else if (registerForm.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (registerForm.password !== registerForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setRegisterErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [registerForm]);

  const handleRegisterSubmit = useCallback(async () => {
    if (!validateRegisterForm()) return;

    try {
      setIsRegistering(true);
      setRegisterApiError('');

      await api.post('/auth/register', {
        name: registerForm.name,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password,
        role: registerForm.role,
        schoolName: registerForm.schoolName,
        schoolType: registerForm.schoolType
      });

      navigate('/dashboard', {
        state: { registrationSuccess: true, email: registerForm.email }
      });
    } catch (err) {
      console.error('Registration error:', err);
      setRegisterApiError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setIsRegistering(false);
    }
  }, [registerForm, validateRegisterForm, navigate]);

  const toggleRegisterPasswordVisibility = useCallback(() => {
    setShowRegisterPassword(prev => !prev);
  }, []);

  // Reset password handlers
  const handleAdminRequestReset = useCallback(async () => {
    if (!adminResetEmail) {
      setResetMessage('⚠️ Please enter your admin email address.');
      return;
    }

    setIsResetting(true);
    setResetMessage('');

    try {
      const response = await api.post('/auth/admin/request-reset', {
        email: adminResetEmail
      });

      setGeneratedCode(response.data.code);
      setResetMessage(`✅ Admin reset code generated: ${response.data.code}. This code expires in 15 minutes.`);
      setResetStep(1);
    } catch (error) {
      if (error.response?.status === 404) {
        setResetMessage(
          '🔒 Admin password reset requires manual process. Please contact system administrator or use the admin panel to generate a reset token.'
        );
      } else {
        const errMsg =
          error.response?.data?.message ||
          '❌ Failed to generate reset code. Please try again or contact administrator.';
        setResetMessage(errMsg);
      }
    } finally {
      setIsResetting(false);
    }
  }, [adminResetEmail]);

  const resetAdminForm = useCallback(() => {
    setResetStep(0);
    setAdminResetEmail('');
    setAdminResetCode('');
    setAdminNewPassword('');
    setAdminConfirmPassword('');
    setResetMessage('');
    setGeneratedCode('');
  }, []);

  const handleAdminReset = useCallback(async () => {
    if (!adminResetCode || !adminNewPassword || !adminConfirmPassword) {
      setResetMessage('⚠️ Please fill in all fields.');
      return;
    }

    if (adminNewPassword !== adminConfirmPassword) {
      setResetMessage('❌ New passwords do not match.');
      return;
    }

    setIsResetting(true);
    setResetMessage('');

    try {
      await api.post('/auth/reset-password-dob', {
        email: adminResetEmail,
        code: adminResetCode,
        newPassword: adminNewPassword
      });

      setResetMessage('✅ Admin password reset successful! You can now log in with your new password.');

      setTimeout(() => {
        setResetModalOpen(false);
        resetAdminForm();
      }, 3000);
    } catch (error) {
      const errMsg = error.response?.data?.message || '❌ Failed to reset password. Please try again.';
      setResetMessage(errMsg);
    } finally {
      setIsResetting(false);
    }
  }, [adminResetCode, adminNewPassword, adminConfirmPassword, adminResetEmail, resetAdminForm]);

  const handleCloseResetModal = useCallback(() => {
    setResetModalOpen(false);
    resetAdminForm();
  }, [resetAdminForm]);

  const handleBackStep = useCallback(() => {
    setResetStep(0);
  }, []);

  // 🎯 Memoized computed values
  const adminResetSteps = useMemo(() =>
    ['Enter Admin Email', 'Enter Reset Code & New Password'],
    []
  );

  const signInCardBackground = theme.palette.mode === 'dark'
    ? 'rgba(30, 41, 59, 0.9)'
    : 'rgba(255, 255, 255, 0.9)';

  const headerContent = useMemo(() => (
    <Box sx={{ mb: isMobile ? 2.5 : 3, textAlign: 'center' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, mb: 3 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            display: 'grid',
            placeItems: 'center',
            background: '#f8fafc',
            border: '1px solid #e5e7eb',
            mx: 'auto'
          }}
        >
          <Box
            component="img"
            src={logo}
            alt="School Logo"
            sx={{
              width: 42,
              height: 42,
              objectFit: 'contain'
            }}
          />
        </Box>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: theme.palette.primary.main, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 }}>
            Admin Portal
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
            My School App
          </Typography>
        </Box>
      </Box>

      <Typography
        variant={isMobile ? "h4" : "h3"}
        component="h1"
        sx={{
          color: theme.palette.text.primary,
          fontWeight: 800,
          letterSpacing: 0,
          mb: 1,
          fontSize: isMobile ? '1.85rem' : '2.4rem',
          lineHeight: 1.08
        }}
      >
        Welcome back
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: isMobile ? '0.95rem' : '1rem',
          fontWeight: 500,
          lineHeight: 1.6
        }}
      >
        Sign in to manage your school dashboard.
      </Typography>
    </Box>
  ), [isMobile, theme]);

  const resetModalContent = useMemo(() => (
    <Dialog
      open={resetModalOpen}
      onClose={handleCloseResetModal}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '24px',
          p: isMobile ? 1 : 2,
          backgroundColor: 'rgba(30, 30, 30, 0.85)',
          backdropFilter: 'blur(40px)',
          WebkitBackdropFilter: 'blur(40px)',
          border: '1px solid rgba(255,255,255,0.08)',
          m: isMobile ? 0 : 'auto',
          maxHeight: isMobile ? '100%' : 'auto',
          color: '#fff'
        }
      }}
    >
      <DialogTitle sx={{
        textAlign: 'center',
        fontWeight: 600,
        color: '#fff',
        fontSize: isMobile ? '1.25rem' : '1.5rem',
        pb: 1,
        pt: isMobile ? 2 : 1
      }}>
        Reset Admin Password
      </DialogTitle>

      <DialogContent>
        <Stepper
          activeStep={resetStep}
          sx={{
            mb: isMobile ? 3 : 4,
            '& .MuiStepLabel-root': {
              padding: isMobile ? '8px' : '16px'
            }
          }}
        >
          {adminResetSteps.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }
                }}
              >
                {isMobile ? label.split(' ')[0] + '...' : label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Stack spacing={isMobile ? 2 : 3}>
          {resetStep === 0 ? (
            <ResetStepOne
              adminResetEmail={adminResetEmail}
              setAdminResetEmail={setAdminResetEmail}
              resetMessage={resetMessage}
              isResetting={isResetting}
              onRequestReset={handleAdminRequestReset}
              isMobile={isMobile}
              theme={theme}
            />
          ) : (
            <ResetStepTwo
              adminResetCode={adminResetCode}
              setAdminResetCode={setAdminResetCode}
              adminNewPassword={adminNewPassword}
              setAdminNewPassword={setAdminNewPassword}
              adminConfirmPassword={adminConfirmPassword}
              setAdminConfirmPassword={setAdminConfirmPassword}
              generatedCode={generatedCode}
              resetMessage={resetMessage}
              isResetting={isResetting}
              onReset={handleAdminReset}
              onBack={handleBackStep}
              isMobile={isMobile}
              theme={theme}
            />
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  ), [
    resetModalOpen, resetStep, adminResetSteps, adminResetEmail, adminResetCode,
    adminNewPassword, adminConfirmPassword, generatedCode, resetMessage, isResetting,
    isMobile, theme, handleAdminRequestReset, handleAdminReset, handleBackStep, handleCloseResetModal
  ]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        position: 'relative',
        overflow: 'hidden',
        background: '#0f172a', // fallback dark navy
        backgroundImage: `
          radial-gradient(circle at 0% 0%, rgba(30, 58, 138, 0.5) 0%, transparent 50%),
          radial-gradient(circle at 100% 0%, rgba(17, 24, 39, 0.8) 0%, transparent 50%),
          radial-gradient(circle at 0% 100%, rgba(30, 41, 59, 0.6) 0%, transparent 50%),
          radial-gradient(circle at 100% 100%, rgba(15, 23, 42, 0.95) 0%, transparent 50%),
          radial-gradient(circle at 85% 50%, rgba(5, 10, 20, 0.85) 0%, transparent 70%)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)',
          backgroundSize: '50px 50px',
          zIndex: 0,
          pointerEvents: 'none'
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.4'/%3E%3C/svg%3E")`,
          opacity: 0.03,
          mixBlendMode: 'overlay',
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      {/* Animated background orbs (subtle, matching navy theme) */}
      <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0 }}>
        <Box
          sx={{
            position: 'absolute',
            top: '-10%',
            left: '-10%',
            width: { xs: '280px', md: '550px' },
            height: { xs: '280px', md: '550px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(96, 165, 250, 0.15) 0%, rgba(96, 165, 250, 0) 70%)',
            filter: 'blur(70px)',
            animation: 'floatOrb1 18s infinite alternate ease-in-out',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '-15%',
            right: '-10%',
            width: { xs: '320px', md: '600px' },
            height: { xs: '320px', md: '600px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(148, 163, 184, 0.1) 0%, rgba(148, 163, 184, 0) 70%)',
            filter: 'blur(80px)',
            animation: 'floatOrb2 22s infinite alternate-reverse ease-in-out',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '40%',
            left: '30%',
            width: { xs: '200px', md: '400px' },
            height: { xs: '200px', md: '400px' },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.12) 0%, rgba(59, 130, 246, 0) 70%)',
            filter: 'blur(70px)',
            animation: 'floatOrb3 24s infinite alternate ease-in-out',
          }}
        />
      </Box>

      {/* Left section: Trust / Marketing content */}
      <Box sx={{
        flex: { xs: 'none', md: 1.5 },
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        p: { xs: 2.5, sm: 4, md: 8, lg: 12 },
        pt: { xs: 3, md: 8 },
        pb: { xs: 4, md: 8 },
        zIndex: 1,
        order: { xs: 2, md: 1 }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <TrustSection isMobile={isMobile} />
        </Box>
      </Box>

      {/* Right section: Card with login/register */}
      <Box sx={{
        flex: { xs: 'none', md: 1 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'transparent',
        p: { xs: 2, sm: 3, md: 6 },
        pt: { xs: 4, md: 6 },
        pb: { xs: 2, md: 6 },
        position: 'relative',
        zIndex: 2,
        order: { xs: 1, md: 2 }
      }}>
        <Fade in timeout={800}>
          <Box sx={{ width: '100%', maxWidth: '440px' }}>
            {/* ✅ Updated Login/Register Card */}
            <Card sx={{
              p: isMobile ? 3 : 5,
              borderRadius: '24px',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 20px 60px rgba(0, 0, 0, 0.5)'
                : '0 20px 60px rgba(0, 0, 0, 0.15)',
              background: signInCardBackground,
              backdropFilter: 'blur(12px)',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.1)'
                : '1px solid rgba(0, 0, 0, 0.08)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {headerContent}

              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth" centered
                  sx={{
                    '& .MuiTab-root': { fontWeight: 600, fontSize: isMobile ? '0.9rem' : '1rem', textTransform: 'none', py: 2, minHeight: 'auto' },
                    '& .Mui-selected': { color: 'primary.main !important' },
                    '& .MuiTabs-indicator': { background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)', height: 3, borderRadius: '3px 3px 0 0' }
                  }}
                >
                  <Tab label="Login" />
                  <Tab label="Register" />
                </Tabs>
              </Box>

              {activeTab === 0 ? (
                <LoginForm theme={theme} isMobile={isMobile} handleChange={handleChange} form={form} handleKeyPress={handleKeyPress} showPassword={showPassword} togglePasswordVisibility={togglePasswordVisibility} error={error} handleSubmit={handleSubmit} isLoading={isLoading} setResetModalOpen={setResetModalOpen} />
              ) : (
                <RegisterForm theme={theme} isMobile={isMobile} registerForm={registerForm} handleRegisterChange={handleRegisterChange} registerErrors={registerErrors} registerApiError={registerApiError} showRegisterPassword={showRegisterPassword} toggleRegisterPasswordVisibility={toggleRegisterPasswordVisibility} handleRegisterSubmit={handleRegisterSubmit} isRegistering={isRegistering} />
              )}

              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                  By continuing, you agree to our <RouterLink to="/terms" style={{ color: '#0a84ff', textDecoration: 'none', fontWeight: 500 }}>Terms and Conditions</RouterLink>
                </Typography>
              </Box>
            </Card>
          </Box>
        </Fade>
      </Box>

      {/* Footer */}
      <Box sx={{
        position: { xs: 'relative', md: 'absolute' },
        left: 0,
        right: 0,
        bottom: { md: 24 },
        width: '100%',
        textAlign: 'center',
        zIndex: 3,
        order: { xs: 3, md: 'initial' },
        px: 2,
        pb: { xs: 3, md: 0 }
      }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', fontWeight: 500, letterSpacing: '0.5px' }}>
          © 2026 CoreVista Technologies. All rights reserved.
        </Typography>
      </Box>

      {resetModalContent}

      {/* Global Styles for Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes floatOrb1 {
          0%   { transform: translate(0, 0) scale(1); }
          50%  { transform: translate(60px, 40px) scale(1.15); }
          100% { transform: translate(-20px, 80px) scale(0.95); }
        }
        @keyframes floatOrb2 {
          0%   { transform: translate(0, 0) scale(1); }
          50%  { transform: translate(-70px, -30px) scale(1.2); }
          100% { transform: translate(30px, -80px) scale(0.9); }
        }
        @keyframes floatOrb3 {
          0%   { transform: translate(0, 0) scale(1) rotate(0deg); }
          50%  { transform: translate(40px, -60px) scale(1.25) rotate(15deg); }
          100% { transform: translate(-50px, 40px) scale(0.95) rotate(-10deg); }
        }
      `}</style>
    </Box>
  );
};

// 🎯 Export memoized component
export default memo(AuthPage);